import React, { useState, useEffect, useRef } from "react";
import "./UploadProgressBar.scss";

interface UploadProgressBarProps {
  percentage: number;
  showPercentage?: boolean;
  height?: number;
}

export default function UploadProgressBar({
  percentage,
  showPercentage = true,
  height = 12,
}: UploadProgressBarProps) {
  const [animatedValue, setAnimatedValue] = useState(0);
  const [displayedPercentage, setDisplayedPercentage] = useState(percentage);
  const debounceRef = useRef<NodeJS.Timeout | undefined>(undefined);

  let col_1, col_2;

  // Same color logic as ProgRing
  if (displayedPercentage === 0) {
    col_1 = "green";
    col_2 = "lime";
  } else if (displayedPercentage > 0 && displayedPercentage < 40) {
    col_1 = "#34D831";
    col_2 = "#B1FB77";
  } else if (displayedPercentage >= 40 && displayedPercentage < 75) {
    col_1 = "#FFDD65";
    col_2 = "#FB9C45";
  } else if (displayedPercentage >= 75 && displayedPercentage < 100) {
    col_1 = "#EE9543";
    col_2 = "#EE5A45";
  } else {
    col_1 = "#DE3635";
    col_2 = "#ED9243";
  }

  // Debounce the percentage updates so they don't change too quickly
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setDisplayedPercentage(percentage);
    }, 300); // 300ms delay before updating the displayed percentage

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [percentage]);

  useEffect(() => {
    let start = animatedValue;
    const duration = 300; // animation duration
    const stepMs = 10;
    const difference = displayedPercentage - start;

    // Only animate if there's a meaningful difference
    if (Math.abs(difference) < 0.5) {
      setAnimatedValue(displayedPercentage);
      return;
    }

    const steps = duration / stepMs;
    const increment = difference / steps;
    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      const newValue = animatedValue + increment * currentStep;

      if (currentStep >= steps) {
        setAnimatedValue(displayedPercentage);
        clearInterval(interval);
      } else {
        setAnimatedValue(newValue);
      }
    }, stepMs);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedPercentage]);

  return (
    <div className="upload-progress-bar-container">
      <div className="progress-bar-wrapper">
        <div
          className="progress-bar-fill"
          style={{
            width: `${animatedValue}%`,
            height: `${height}px`,
            background: `linear-gradient(90deg, ${col_1} 0%, ${col_2} 100%)`,
          }}
        />
      </div>
      {showPercentage && (
        <div className="progress-percentage">{Math.round(animatedValue)}%</div>
      )}
    </div>
  );
}
